// jsPDF is loaded lazily inside the exported functions so it isn't bundled into the
// landing-page chunk (saves ~445 KiB of unused JS on every non-Results pageview).
let _jsPDFCtor = null;
async function getJsPDF() {
  if (_jsPDFCtor) return _jsPDFCtor;
  const mod = await import(/* webpackChunkName: "jspdf" */ "jspdf");
  _jsPDFCtor = mod.jsPDF;
  return _jsPDFCtor;
}

/**
 * Generate a thyroid analysis PDF from diagnosis data.
 * Uses jsPDF text API directly — no html2canvas, no empty pages.
 */
function generateThyroidPdf(jsPDF, diagnosis, referenceRanges, i18n) {
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginL = 18;
  const marginR = 18;
  const contentW = pageW - marginL - marginR;
  const bottomLimit = pageH - 20;
  let y = 18;

  const lang = i18n?.language || "et";
  const t = (key) => i18n?.t?.(key) ?? key;

  const TEST_UNITS = {
    tsh: "mIU/L", ft4: "pmol/L", ft3: "pmol/L", atpo: "kU/L",
    tsi: "U/L", tg: "mcg/L", atg: "kU/L", calcitonin: "pmol/L",
    leucocytes: "×10⁹/L", crp: "mg/L",
  };

  const TEST_NAMES = {
    tsh: "TSH", ft4: "fT4", ft3: "fT3", atpo: "aTPO",
    tsi: "TSI", tg: "TG", atg: "aTG", calcitonin: "Kaltsitoniin",
    leucocytes: "Leukotsüüdid", crp: "CRP",
  };

  // ── helpers ──
  function ensureSpace(need) {
    if (y + need > bottomLimit) {
      doc.addPage();
      y = 18;
    }
  }

  function drawHeader() {
    // Blue banner
    doc.setFillColor(14, 165, 233); // sky-500
    doc.rect(0, 0, pageW, 28, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("Kilpnäärme Kliinik", marginL, 13);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const dateStr = diagnosis?.created_at
      ? new Date(diagnosis.created_at).toLocaleDateString(
          lang === "et" ? "et-EE" : lang === "ru" ? "ru-RU" : "en-US",
          { year: "numeric", month: "long", day: "numeric" }
        )
      : "";
    doc.text(dateStr, marginL, 22);
    y = 36;
    doc.setTextColor(0, 0, 0);
  }

  function drawTestTable() {
    const tests = diagnosis?.tests || {};
    const entries = Object.entries(tests).filter(
      ([k, v]) => v !== null && v !== undefined && TEST_UNITS[k]
    );
    if (entries.length === 0) return;

    ensureSpace(10 + entries.length * 7);

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(t("results.testResults"), marginL, y);
    y += 6;

    // Table header
    doc.setFillColor(241, 245, 249); // slate-100
    doc.rect(marginL, y - 4, contentW, 7, "F");
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text("Test", marginL + 2, y);
    doc.text(t("results.result") || "Result", marginL + 45, y);
    doc.text(t("results.unit") || "Unit", marginL + 80, y);
    doc.text(t("results.reference") || "Reference", marginL + 105, y);
    y += 6;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    entries.forEach(([key, value]) => {
      ensureSpace(7);
      const displayVal = key === "tsh" ? Number(value).toFixed(3) : String(value);
      const unit = TEST_UNITS[key];
      const name = TEST_NAMES[key] || key.toUpperCase();
      let ref = "N/A";
      if (
        key === "tsh" &&
        diagnosis?.questionnaire?.thyroidCancer === "yes"
      ) {
        ref = "0.1-0.5 mIU/L";
      } else if (referenceRanges?.[key]?.range) {
        ref = referenceRanges[key].range;
      }

      doc.text(name, marginL + 2, y);
      doc.text(displayVal, marginL + 45, y);
      doc.text(unit, marginL + 80, y);
      doc.text(ref, marginL + 105, y);
      // light line
      doc.setDrawColor(226, 232, 240);
      doc.line(marginL, y + 2, marginL + contentW, y + 2);
      y += 7;
    });

    y += 4;
  }

  function writeMarkdownText(text) {
    if (!text) return;

    // Clean up
    let cleaned = text
      .replace(/```markdown\s*/gi, "")
      .replace(/```\s*/g, "")
      .replace(/MISSING TESTS that should be recommended:.*$/gm, "")
      .replace(/PUUDUVAD ANALÜÜSID.*$/gm, "")
      .replace(/НЕДОСТАЮЩИЕ АНАЛИЗЫ.*$/gm, "")
      .trim();

    const lines = cleaned.split("\n");

    for (const line of lines) {
      const trimmed = line.trim();

      // skip empty
      if (trimmed === "") {
        y += 3;
        continue;
      }

      // Headings
      if (trimmed.startsWith("### ")) {
        ensureSpace(10);
        y += 3;
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        const heading = trimmed.replace("### ", "");
        const wrappedH = doc.splitTextToSize(heading, contentW);
        wrappedH.forEach((l) => {
          ensureSpace(6);
          doc.text(l, marginL, y);
          y += 5;
        });
        y += 1;
        continue;
      }
      if (trimmed.startsWith("## ")) {
        ensureSpace(10);
        y += 3;
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        const heading = trimmed.replace("## ", "");
        const wrappedH = doc.splitTextToSize(heading, contentW);
        wrappedH.forEach((l) => {
          ensureSpace(6);
          doc.text(l, marginL, y);
          y += 5.5;
        });
        y += 1;
        continue;
      }
      if (trimmed.startsWith("# ")) {
        ensureSpace(12);
        y += 4;
        doc.setFontSize(13);
        doc.setFont("helvetica", "bold");
        const heading = trimmed.replace("# ", "");
        const wrappedH = doc.splitTextToSize(heading, contentW);
        wrappedH.forEach((l) => {
          ensureSpace(7);
          doc.text(l, marginL, y);
          y += 6;
        });
        y += 2;
        continue;
      }

      // Bold section headers like **1. Üldine hinnang**
      const boldMatch = trimmed.match(/^\*\*(.+)\*\*$/);
      if (boldMatch) {
        ensureSpace(10);
        y += 3;
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        const wrappedB = doc.splitTextToSize(boldMatch[1], contentW);
        wrappedB.forEach((l) => {
          ensureSpace(6);
          doc.text(l, marginL, y);
          y += 5;
        });
        y += 1;
        continue;
      }

      // Bullet points
      if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
        const bulletText = trimmed.replace(/^[-*]\s+/, "");
        writeBulletLine(bulletText);
        continue;
      }

      // Numbered lists
      const numMatch = trimmed.match(/^(\d+)\.\s+(.+)/);
      if (numMatch) {
        const listText = `${numMatch[1]}. ${numMatch[2]}`;
        writeBulletLine(listText, false);
        continue;
      }

      // Regular paragraph — may contain inline **bold**
      writeParagraphLine(trimmed);
    }
  }

  function writeBulletLine(text, useDot = true) {
    doc.setFontSize(10);
    const prefix = useDot ? "  •  " : "  ";
    const plainText = text.replace(/\*\*/g, "");
    const wrapped = doc.splitTextToSize(prefix + plainText, contentW - 4);
    wrapped.forEach((l, idx) => {
      ensureSpace(5);
      doc.setFont("helvetica", "normal");
      // Bold sub-headings in bullet points (e.g., "Ravimi muutused:")
      if (idx === 0 && text.includes("**")) {
        const parts = text.split(/\*\*/);
        let xPos = marginL + 2;
        doc.text(prefix, xPos, y);
        xPos += doc.getTextWidth(prefix);
        parts.forEach((part, i) => {
          if (i % 2 === 1) {
            doc.setFont("helvetica", "bold");
          } else {
            doc.setFont("helvetica", "normal");
          }
          const partWrapped = doc.splitTextToSize(part, contentW - (xPos - marginL));
          if (partWrapped[0]) {
            doc.text(partWrapped[0], xPos, y);
            xPos += doc.getTextWidth(partWrapped[0]);
          }
        });
      } else {
        doc.text(idx === 0 ? l : "      " + l.trim(), marginL + 2, y);
      }
      y += 4.5;
    });
    y += 1;
  }

  function writeParagraphLine(text) {
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const plainText = text.replace(/\*\*/g, "");
    const wrapped = doc.splitTextToSize(plainText, contentW);
    wrapped.forEach((l) => {
      ensureSpace(5);
      doc.text(l, marginL, y);
      y += 4.5;
    });
    y += 2;
  }

  // ── Build PDF ──
  drawHeader();
  drawTestTable();

  // Separator line
  doc.setDrawColor(200, 200, 200);
  doc.line(marginL, y, marginL + contentW, y);
  y += 6;

  // Analysis text
  writeMarkdownText(diagnosis?.result);

  // Footer on each page
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Kilpnäärme Kliinik  •  www.kilpnaarmekliinik.ee  •  ${i}/${totalPages}`,
      pageW / 2,
      pageH - 8,
      { align: "center" }
    );
    doc.setTextColor(0, 0, 0);
  }

  return doc;
}

/**
 * Download the PDF.
 */
export async function downloadThyroidPdf(diagnosis, referenceRanges, i18n) {
  const jsPDF = await getJsPDF();
  const doc = generateThyroidPdf(jsPDF, diagnosis, referenceRanges, i18n);
  const dateStr = diagnosis?.created_at
    ? new Date(diagnosis.created_at).toISOString().split("T")[0]
    : "report";
  doc.save(`thyroid-analysis-${dateStr}.pdf`);
}

/**
 * Get PDF as base64 string (for email attachment).
 */
export async function getThyroidPdfBase64(diagnosis, referenceRanges, i18n) {
  const jsPDF = await getJsPDF();
  const doc = generateThyroidPdf(jsPDF, diagnosis, referenceRanges, i18n);
  // doc.output("datauristring") returns "data:application/pdf;base64,..."
  const dataUri = doc.output("datauristring");
  return dataUri.split(",")[1];
}
